import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  ViewService
} from "../../../services/common/view.service";
import { AlertPopupService } from '../../../services/common/alert-popup.service';
import { DataService } from '../../../services/common/data.service';
import { AlertFormModalPage } from '../alert-form-modal/alert-form-modal.page';

@Component({
  selector: 'app-alert-popup',
  templateUrl: './alert-popup.page.html',
  styleUrls: ['./alert-popup.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AlertPopupPage implements OnInit {

  // componentProps
  @Input() alert: any;
  @Input() view: ViewService;

  // resolved localized strings
  resolvedTitle: string = '';
  resolvedBody: string = '';
  resolvedBodySafe: SafeHtml = '';
  resolvedCtaText: string = '';

  // close action for app-page-title
  closeAction: any;

  // listener
  listener = {
    view: null,
  };

  constructor(
    private cd: ChangeDetectorRef,
    private modalCtrl: ModalController,
    private alertPopupService: AlertPopupService,
    private sanitizer: DomSanitizer,
    public data: DataService,
    // Istanza reale di ViewService (DI). NOTA: l'@Input() view qui sopra riceve
    // la CLASSE ViewService (vedi data.service.ts), usata solo per membri static
    // come isMobile/iconsMap. Per chiamare metodi di istanza come modalAlert()
    // serve l'istanza vera, che iniettiamo qui.
    private viewInstance: ViewService,
    private elRef: ElementRef
  ) {}

  ngOnInit() {
    // HARD GUARD: l'alert-popup NON deve MAI apparire su mobile.
    // Anche se checkAndShowAlertPopup() ha già questa guardia, la ripetiamo qui come
    // rete di sicurezza: se un nuovo punto di ingresso dovesse creare il modal
    // bypassando il flusso standard, lo chiudiamo immediatamente.
    if (ViewService.isMobile) {
      console.log('[AlertPopup] hard guard: mobile detected -> dismissing modal');
      this.modalCtrl.dismiss();
      return;
    }

    // Resolve multi-language fields
    this.resolveLanguage();

    this.closeAction = () => { this.onDismiss(); };

    // Track impression subito (ionViewDidEnter nei modal può non partire)
    this.trackImpression();
  }

  /**
   * Resolve multi-language fields from alert object.
   * Uses organization.translate_language with fallback to 'it'.
   */
  private resolveLanguage() {
    const lang = this.data?.selOrganization?.translate_language || 'it';
    const fallback = 'it';

    this.resolvedTitle = this.resolveLang(this.alert?.title, lang, fallback);
    this.resolvedBody = this.resolveLang(this.alert?.body, lang, fallback);
    // Bypass della sanitization di Angular per permettere stili inline (es. style="color:..."),
    // classi Quill (ql-size-*, ql-align-*) e tag HTML custom scritti dall'admin.
    // SICUREZZA: il contenuto è controllato da admin fidati (solo staff interno), quindi
    // l'XSS risk è accettabile. Non usare bypassSecurityTrustHtml con contenuto user-generated.
    this.resolvedBodySafe = this.sanitizer.bypassSecurityTrustHtml(this.resolvedBody || '');

    // Se non c'è CTA (cta_type === 'none' o mancante) il pulsante principale
    // deve SEMPRE mostrare "Chiudi", ignorando completamente i campi
    // cta_config.label / cta_config.button_text che possono essere rimasti
    // popolati da una configurazione precedente (es. l'admin aveva messo
    // "Sono interessato" quando il CTA era info_request, poi ha cambiato
    // in "Nessuno" senza ripulire il testo del pulsante).
    if (!this.hasCta) {
      this.resolvedCtaText = 'Chiudi';
      return;
    }

    // Con CTA attivo usiamo le label configurate dall'admin, con fallback a OK.
    this.resolvedCtaText = this.resolveLang(this.alert?.cta_config?.label, lang, fallback)
      || this.resolveLang(this.alert?.cta_config?.button_text, lang, fallback)
      || this.alert?.cta_text
      || 'OK';
  }

  /**
   * True quando l'avviso ha un CTA reale (link, info_request, ticket, custom_form).
   * Usato sia per etichettare il pulsante principale sia per nascondere il
   * pulsante "Chiudi" di sinistra ed evitare la duplicazione visiva.
   */
  get hasCta(): boolean {
    const t = this.alert?.cta_type;
    return !!t && t !== 'none';
  }

  private resolveLang(field: any, lang: string, fallback: string): string {
    if (!field) return '';
    if (typeof field === 'string') return field;
    return field[lang] || field[fallback] || '';
  }

  resolveLangPublic(field: any): string {
    const lang = this.data?.selOrganization?.translate_language || 'it';
    return this.resolveLang(field, lang, 'it');
  }

  ionViewDidEnter() {
    this.listenerView();
    // Se il contenuto della modale produce uno scroll interno, allarga la modale
    // di 100px (una sola volta). Aspetta un tick affinché il rich-html sia renderizzato.
    setTimeout(() => this.adjustHeightIfScrolling(), 50);
  }

  /**
   * Rileva se l'ion-content interno sta producendo uno scroll. In quel caso
   * aumenta la --min-height (e la --max-height se più piccola) della modale di 100px,
   * limitato comunque dal viewport corrente.
   */
  private adjustHeightIfScrolling() {
    try {
      const host: HTMLElement | null = this.elRef?.nativeElement;
      if (!host) return;
      const modalEl: HTMLElement | null = host.closest('ion-modal');
      if (!modalEl) return;

      const ionContent: any = host.querySelector('ion-content');
      if (!ionContent) return;
      const inner: HTMLElement | null =
        ionContent.shadowRoot?.querySelector('.inner-scroll') || null;
      if (!inner) return;

      const overflow = inner.scrollHeight - inner.clientHeight;
      if (overflow <= 1) return; // nessuno scroll → nessuna modifica

      const styles = getComputedStyle(modalEl);
      const currentMin = parseFloat(styles.getPropertyValue('--min-height')) || modalEl.clientHeight;
      const currentMax = parseFloat(styles.getPropertyValue('--max-height')) || window.innerHeight;
      const viewportCap = Math.max(0, window.innerHeight - 32);

      const newMin = Math.min(currentMin + 100, viewportCap);
      const newMax = Math.min(Math.max(currentMax, newMin), viewportCap);

      modalEl.style.setProperty('--min-height', `${newMin}px`);
      modalEl.style.setProperty('--max-height', `${newMax}px`);
    } catch (err) {
      console.warn('[AlertPopup] adjustHeightIfScrolling error', err);
    }
  }

  ionViewWillLeave() {
    if (this.listener.view) {
      this.listener.view.unsubscribe();
    }
  }

  async dismiss() {
    await this.modalCtrl.dismiss();
  }

  async onDismiss() {
    try {
      if (this.alert && this.alert._id) {
        await this.alertPopupService.trackDismiss(this.alert._id);
      }
    } catch (err) {
      console.error('Error tracking dismiss:', err);
    }
    await this.modalCtrl.dismiss({ action: 'dismiss' });
  }

  async onCtaClick() {
    try {
      const cta_type = this.alert?.cta_type;

      // Se non c'è CTA il pulsante principale è un semplice "Chiudi":
      // non ha senso tracciare un cta_click fittizio, deleghiamo a onDismiss
      // che traccia correttamente il dismiss.
      if (!this.hasCta) {
        await this.onDismiss();
        return;
      }

      // Track click del CTA sempre (a prescindere dal tipo)
      if (this.alert && this.alert._id) {
        try { await this.alertPopupService.trackCtaClick(this.alert._id); }
        catch (e) { console.error('trackCtaClick error', e); }
      }

      if (cta_type === 'link') {
        await this.handleLinkCta();
        await this.showThankYouIfConfigured();
        await this.modalCtrl.dismiss({ action: 'cta_click', alert: this.alert });
        return;
      }

      if (cta_type === 'info_request') {
        await this.handleInfoRequestCta();
        await this.showThankYouIfConfigured();
        await this.modalCtrl.dismiss({ action: 'cta_click', alert: this.alert });
        return;
      }

      if (cta_type === 'richiesta_contatto') {
        // Nessun form: il backend crea una richiesta_contatto con origin=6
        // usando i dati dell'organization loggata.
        await this.handleRichiestaContattoCta();
        await this.showThankYouIfConfigured();
        await this.modalCtrl.dismiss({ action: 'cta_click', alert: this.alert });
        return;
      }

      if (cta_type === 'ticket' || cta_type === 'custom_form') {
        // Apri il modal del form. Il popup avviso NON si chiude: resta aperto
        // sotto finché l'utente non conferma o annulla il form.
        await this.openFormModal(cta_type);
        return;
      }

      // Nessun tipo speciale: chiudo e basta
      await this.modalCtrl.dismiss({ action: 'cta_click', alert: this.alert });
    } catch (err) {
      console.error('Error handling CTA click:', err);
    }
  }

  private async handleLinkCta() {
    const url = this.alert?.cta_config?.url;
    if (url) {
      window.open(url, '_blank');
    }
  }

  private async handleInfoRequestCta() {
    try {
      const payload = this.alert?.cta_config?.payload || {};
      if (this.alert && this.alert._id) {
        await this.alertPopupService.trackCtaSubmit(
          this.alert._id,
          'info_request',
          payload
        );
      }
    } catch (err) {
      console.error('Error submitting info request:', err);
    }
  }

  /**
   * CTA "richiesta_contatto": come info_request non apre un form, ma chiama subito
   * trackCtaSubmit con cta_type='richiesta_contatto'. Il backend creera' una
   * richiesta_contatto con origin=6 popolata dai dati dell'organization loggata.
   */
  private async handleRichiestaContattoCta() {
    try {
      if (this.alert && this.alert._id) {
        await this.alertPopupService.trackCtaSubmit(
          this.alert._id,
          'richiesta_contatto',
          {}
        );
      }
    } catch (err) {
      console.error('Error submitting richiesta_contatto:', err);
    }
  }

  /**
   * Apre il modal dedicato con il form CTA (custom_form o ticket).
   * Il popup avviso resta aperto sotto: se l'utente annulla può rileggere il
   * contenuto o chiudere. Solo al submit confermato chiudiamo tutto.
   *
   * NOTA cssClass: usiamo `modal_alarm` (stessa classe del popup avviso padre)
   * così il form ha la stessa larghezza/altezza del popup sotto.
   */
  private async openFormModal(cta_type: 'custom_form' | 'ticket') {
    // Risolvo la descrizione form nella lingua utente (fallback 'it')
    const formDescription = this.resolveLangPublic(this.alert?.cta_config?.form_description);

    const modal = await this.modalCtrl.create({
      component: AlertFormModalPage,
      componentProps: {
        alert: this.alert,
        view: this.view,
        ctaLabel: this.resolvedCtaText,
        // Riusa il titolo dell'avviso padre come titolo del form modal,
        // così l'utente mantiene il contesto visivo durante la compilazione.
        title: this.resolvedTitle,
        formDescription: formDescription
      },
      backdropDismiss: false,
      cssClass: 'modal_alarm'
    });
    await modal.present();
    const { data } = await modal.onDidDismiss();

    if (data?.submitted) {
      // Submit confermato dall'utente: traccia invio ed esci dal popup avviso
      try {
        if (this.alert && this.alert._id) {
          await this.alertPopupService.trackCtaSubmit(
            this.alert._id,
            cta_type,
            data.data || {}
          );
        }
      } catch (err) {
        console.error('Error submitting CTA form:', err);
      }
      // Modal di ringraziamento (se configurato) PRIMA di chiudere il popup avviso,
      // così l'utente vede la conferma invio.
      await this.showThankYouIfConfigured();
      await this.modalCtrl.dismiss({ action: 'cta_submit', alert: this.alert, data: data.data });
    }
    // Se l'utente ha annullato (submitted=false), il popup avviso resta aperto
    // così può rileggere il contenuto o cliccare "Chiudi".
  }

  /**
   * Se l'admin ha configurato `cta_config.thank_you_text`, mostra un modal
   * grafico di conferma (icona "succ") e aspetta che l'utente lo chiuda prima
   * di procedere con la dismiss del popup avviso.
   *
   * Usato per TUTTI i CTA che comportano un'azione (link, info_request,
   * ticket, custom_form). Se il testo è vuoto, no-op: nessun modal mostrato.
   */
  private async showThankYouIfConfigured(): Promise<void> {
    try {
      const text = this.resolveLangPublic(this.alert?.cta_config?.thank_you_text);
      console.log('[AlertPopup] thank-you text resolved:', text, 'raw:', this.alert?.cta_config?.thank_you_text);
      if (!text || !text.trim()) {
        console.log('[AlertPopup] no thank-you text configured -> skipping modal');
        return;
      }
      // IMPORTANTE: usiamo viewInstance (iniettata via DI), NON this.view che è
      // la CLASSE ViewService (passata via componentProps solo per i membri static).
      if (!this.viewInstance || typeof this.viewInstance.modalAlert !== 'function') {
        console.warn('[AlertPopup] viewInstance.modalAlert not available');
        return;
      }

      const title = this.resolvedTitle || 'Grazie';
      const modal: any = await this.viewInstance.modalAlert(title, text, 'succ', 'OK');
      if (modal && typeof modal.onDidDismiss === 'function') {
        await modal.onDidDismiss();
      }
    } catch (err) {
      console.error('[AlertPopup] error showing thank-you modal:', err);
    }
  }

  private async trackImpression() {
    try {
      if (this.alert && this.alert._id) {
        console.log('[AlertPopup] trackImpression() calling for alert', this.alert._id);
        const res = await this.alertPopupService.trackImpression(this.alert._id);
        console.log('[AlertPopup] trackImpression OK', res);
      } else {
        console.warn('[AlertPopup] trackImpression SKIPPED - no alert._id');
      }
    } catch (err) {
      console.error('[AlertPopup] Error tracking impression:', err);
    }
  }

  // listener
  listenerView() {
    this.listener.view = ViewService.updateView$.subscribe((obj?: any) => {
      this.view.pipeChanged++;
      this.cd.markForCheck();
    });
  }
}
